import React from 'react';
import { LandingNav } from '../components/Nav';
import SiteFooter from '../components/SiteFooter';
import { CHAPTER_SECTION_SCROLL_MARGIN_PX } from '../hooks/useChapterSectionNav';
import NovaAvatarImg from '../components/NovaAvatarImg';

const ActLabel = ({ text }) => (
  <div
    style={{
      display: 'inline-flex',
      background: 'rgb(251,234,240)',
      borderRadius: '20px',
      padding: '4px 12px',
      marginBottom: '14px',
    }}
  >
    <span style={{ fontSize: '11px', fontWeight: 600, color: 'rgb(153,53,86)' }}>{text}</span>
  </div>
);

const SectionHeader = ({ act, headline, italicLine, subhead }) => (
  <div style={{ textAlign: 'center', marginBottom: '24px' }}>
    <ActLabel text={act} />
    <h2
      className="serif"
      style={{
        fontSize: 'clamp(24px, 2.5vw, 28px)',
        fontWeight: 700,
        textAlign: 'center',
        color: 'rgb(17,17,17)',
        lineHeight: 1.25,
        marginBottom: '10px',
      }}
    >
      {headline}
      <br />
      <em style={{ color: 'rgb(212,83,126)' }}>{italicLine}</em>
    </h2>
    <p
      style={{
        fontSize: '15px',
        color: 'rgb(122,107,99)',
        textAlign: 'center',
        maxWidth: '540px',
        margin: '0 auto',
        lineHeight: 1.65,
      }}
    >
      {subhead}
    </p>
  </div>
);

const FeatCard = ({ emoji, title, desc, isNew, borderColor }) => (
  <div
    style={{
      background: 'rgb(255,253,249)',
      border: '0.5px solid rgb(232,224,216)',
      borderRadius: '14px',
      padding: '18px',
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      borderLeft: `3px solid ${borderColor}`,
    }}
  >
    <div style={{ fontSize: '22px', marginBottom: '10px' }}>{emoji}</div>
    <p style={{ fontSize: '13px', fontWeight: 700, color: 'rgb(17,17,17)', marginBottom: '5px', lineHeight: 1.3 }}>
      {title}
      {isNew && (
        <span
          style={{
            fontSize: '9px',
            fontWeight: 700,
            background: 'rgb(225,245,238)',
            color: 'rgb(15,110,86)',
            padding: '1px 6px',
            borderRadius: '8px',
            marginLeft: '6px',
            verticalAlign: 'middle',
          }}
        >
          New
        </span>
      )}
    </p>
    <p style={{ fontSize: '12px', color: 'rgb(95,94,90)', lineHeight: 1.5, margin: 0 }}>{desc}</p>
  </div>
);

const CardHead = ({ title, sub, right }) => (
  <div
    style={{
      padding: '12px 16px',
      borderBottom: '1.5px solid rgb(212,83,126)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexShrink: 0,
    }}
  >
    <div>
      <p style={{ fontSize: '13px', fontWeight: 600, color: 'rgb(17,17,17)', margin: 0 }}>{title}</p>
      {sub && <p style={{ fontSize: '11px', color: 'rgb(122,107,99)', marginTop: '1px', marginBottom: 0 }}>{sub}</p>}
    </div>
    {right}
  </div>
);

export default function TTCChapterPage() {
  const trackFeatures = [
    {
      emoji: '📅',
      title: 'Cycle & fertile window tracking',
      desc: "Log your cycle and know exactly when you're most fertile. Nova reminds you on the days that matter most.",
      isNew: false,
      borderColor: 'rgb(251,234,240)',
    },
    {
      emoji: '🌡️',
      title: 'BBT logging',
      desc: 'Track basal body temperature daily. Nova charts your curve and flags the thermal shift that confirms ovulation.',
      isNew: true,
      borderColor: 'rgb(225,245,238)',
    },
    {
      emoji: '🧪',
      title: 'OPK & cervical mucus tracking',
      desc: 'Log LH strips and CM observations. Together they give you the clearest picture of your fertile window.',
      isNew: true,
      borderColor: 'rgb(238,237,254)',
    },
    {
      emoji: '🔍',
      title: 'Cycle pattern analysis',
      desc: "After 2-3 cycles Nova spots what you'd miss — short luteal phase, irregular ovulation, shifting fertile windows.",
      isNew: true,
      borderColor: 'rgb(255,241,221)',
    },
    {
      emoji: '📝',
      title: 'Medicated cycle tracking',
      desc: 'On Clomid, letrozole, or a trigger shot? Log your medicated cycle alongside your natural data so Nova holds the full picture.',
      isNew: true,
      borderColor: 'rgb(230,241,251)',
    },
  ];

  const oraInsights = [
    {
      title: '📊 Fertile window shifted early',
      desc: 'Your last 3 cycles peaked Day 12-14. This cycle your LH surge came on Day 10 — 2 days earlier than usual.',
    },
    {
      title: '🌡️ Thermal shift confirmed ovulation',
      desc: 'BBT rose 0.4°F after your LH surge — confirming ovulation around Day 10. CM peaked the same day.',
    },
    {
      title: '⏱️ Luteal phase has been short',
      desc: "10-11 days for 3 cycles in a row. Worth mentioning to your doctor if you don't conceive in the next cycle.",
    },
  ];

  const oraArticles = [
    { title: 'What a short luteal phase means for conception', time: '4 min read' },
    { title: 'How to use BBT and OPKs together', time: '3 min read' },
    { title: 'Why your fertile window might be shifting', time: '5 min read' },
  ];

  const learnFeatures = [
    {
      emoji: '🤔',
      title: '"Is this normal?" checker',
      desc: 'Type a symptom. Nova answers relative to your exact cycle day and history.',
      isNew: true,
      borderColor: 'rgb(238,237,254)',
    },
    {
      emoji: '📚',
      title: 'Cycle school',
      desc: 'Luteal phase, OPKs, fertile windows — interactive explainers tied to where you are in your cycle.',
      isNew: false,
      borderColor: 'rgb(225,245,238)',
    },
    {
      emoji: '🧬',
      title: 'Lab result decoder',
      desc: 'AMH, FSH, progesterone at 7DPO — Nova explains what your numbers mean for your cycle.',
      isNew: true,
      borderColor: 'rgb(255,241,221)',
    },
    {
      emoji: '🔬',
      title: 'Condition flags',
      desc: 'PCOS, thyroid, endometriosis — Nova flags patterns that might point to an underlying condition.',
      isNew: true,
      borderColor: 'rgb(251,234,240)',
    },
  ];

  const oraSpaces = [
    {
      emoji: '💜',
      name: 'TTC — First Year',
      desc: 'Natural conception · cycle tracking · support',
      tag: 'Matched',
      tagBg: '#EDE8FE',
      tagColor: '#4A30A0',
    },
    {
      emoji: '📅',
      name: 'Ovulation & Timing',
      desc: 'OPKs, fertile windows, cycle tips',
      tag: 'Matched',
      tagBg: '#EDE8FE',
      tagColor: '#4A30A0',
    },
    {
      emoji: '⏳',
      name: 'TWW Warriors',
      desc: 'Two-week wait · symptom spotting · support',
      tag: 'Live now',
      tagBg: '#EDE8FE',
      tagColor: '#4A30A0',
    },
    {
      emoji: '🕊️',
      name: 'After Loss — Trying Again',
      desc: 'Miscarriage, chemical pregnancy, hope',
      tag: 'Support',
      tagBg: '#E8EEF8',
      tagColor: '#2A4A8A',
    },
    {
      emoji: '📍',
      name: 'Columbus, OH TTC Chat',
      desc: 'Local clinic recs · Ohio fertility community',
      tag: 'Local',
      tagBg: '#E3F5EC',
      tagColor: '#1A6B42',
    },
    {
      emoji: '🌱',
      name: 'Natural TTC — Month 6+',
      desc: 'Still trying naturally · next steps · when to escalate',
      tag: 'Support',
      tagBg: '#FEF3E2',
      tagColor: '#8A5010',
    },
  ];

  const liveRooms = [
    { color: 'rgb(29,158,117)', name: 'TWW support — anyone awake?', sub: '14 women live now' },
    { color: 'rgb(29,158,117)', name: 'After a negative — processing together', sub: '6 women live now' },
    { color: 'rgb(136,135,128)', name: 'Cycle buddies — DPO 8-10 check-in', sub: 'Tomorrow 8pm ET' },
  ];

  const connections = [
    { initials: 'JL', bg: 'rgb(212,83,126)', name: 'Jess L.', desc: 'Day 22 · 8DPO · first cycle tracking', match: '98%' },
    { initials: 'MK', bg: 'rgb(127,119,221)', name: 'Maya K.', desc: 'Day 21 · 7DPO · using OPKs', match: '94%' },
    { initials: 'SR', bg: 'rgb(45,175,138)', name: 'Sara R.', desc: 'Day 23 · 9DPO · TTC after loss', match: '91%' },
  ];

  const decideFeatures = [
    {
      emoji: '⏱️',
      title: 'When to see a doctor',
      desc: 'How long to try naturally based on your age — and what to expect at a first fertility workup.',
      isNew: false,
      borderColor: 'rgb(225,245,238)',
    },
    {
      emoji: '📋',
      title: 'Appointment prep kit',
      desc: 'Nova builds a summary of your cycle data and questions to bring to your OB or RE consult.',
      isNew: true,
      borderColor: 'rgb(251,234,240)',
    },
    {
      emoji: '🏥',
      title: 'Specialist matching',
      desc: 'OB, RE, or reproductive immunologist — matched by your situation, location, and insurance.',
      isNew: false,
      borderColor: 'rgb(238,237,254)',
    },
    {
      emoji: '💬',
      title: 'Scripts for hard conversations',
      desc: 'Advocate for earlier testing, ask for an RPL workup, or find the words for your partner.',
      isNew: true,
      borderColor: 'rgb(230,241,251)',
    },
    {
      emoji: '🤝',
      title: 'Partner support guide',
      desc: 'How to talk to your partner about TTC stress, timing pressure, and when to consider joint fertility testing.',
      isNew: true,
      borderColor: 'rgb(238,237,254)',
    },
    {
      emoji: '🕊️',
      title: 'After a loss — what comes next',
      desc: 'What to do after a miscarriage or chemical pregnancy — medically, emotionally, and what to ask for.',
      isNew: true,
      borderColor: 'rgb(255,241,221)',
    },
  ];

  const pathForward = [
    {
      title: 'Track 2-3 cycles',
      desc: "Build a baseline with BBT, OPKs, and cycle length. Nova spots what's worth noting.",
      nova: 'Nova is tracking this with you →',
    },
    {
      title: '6 months TTC — review your data',
      desc: 'Nova generates a cycle summary — patterns, timing, and anything irregular — ready to share with a doctor.',
      nova: 'Nova prepares your cycle report →',
    },
    {
      title: 'Book a fertility workup',
      desc: 'CD3 bloodwork, progesterone at 7DPO, semen analysis. Nova tells you what to ask for and explains every result.',
      nova: 'Nova decodes your lab results →',
    },
    {
      title: 'First RE consultation',
      desc: 'Nova prepares your consultation summary — history, patterns, and questions so you walk in confident.',
      nova: 'Nova builds your RE prep kit →',
    },
    {
      title: 'After a negative or a loss',
      desc: 'What to do medically and emotionally — when to try again, what tests to request, and Nova staying with you.',
      nova: 'Nova holds the harder roads too →',
    },
    {
      title: 'Moving to IVF or IUI',
      desc: 'When the time comes, Nova transitions with you — connecting you to the Fertility chapter with your full TTC history intact.',
      nova: 'Nova carries your story forward →',
    },
  ];

  const sectionWhite = { padding: '52px 48px', backgroundColor: 'rgb(255,252,249)' };
  const sectionCream = { padding: '52px 48px', backgroundColor: 'rgb(249,246,241)' };
  const sectionAnchor = { scrollMarginTop: `${CHAPTER_SECTION_SCROLL_MARGIN_PX}px` };
  const inner = { maxWidth: '1100px', margin: '0 auto' };
  const tabPink = 'rgb(212,83,126)';
  const commRow = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 16px',
    borderBottom: '0.5px solid rgb(232,224,216)',
  };

  return (
    <div style={{ background: 'rgb(255,252,249)' }}>
      <div style={{ background: 'var(--white)', color: 'var(--text)' }}>
        <LandingNav />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', minHeight: '480px' }}>
        <div
          style={{
            backgroundImage: "url('/images/chapter-heroes/cycle.png')",
            backgroundSize: 'cover',
            backgroundPosition: 'top center',
            minHeight: '480px',
          }}
        />
        <div
          style={{
            background: 'rgb(127,119,221)',
            padding: '52px 48px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255,255,255,0.15)',
              borderRadius: '20px',
              padding: '5px 12px',
              marginBottom: '16px',
              width: 'fit-content',
            }}
          >
            <span style={{ fontSize: '16px' }}>💜</span>
            <span style={{ fontSize: '12px', fontWeight: 600, color: 'white' }}>Trying to Conceive</span>
            <span
              style={{
                fontSize: '10px',
                color: 'rgba(255,255,255,0.7)',
                background: 'rgba(255,255,255,0.15)',
                padding: '2px 8px',
                borderRadius: '10px',
              }}
            >
              Coming 2026
            </span>
          </div>
          <h1 className="serif" style={{ fontSize: 'clamp(28px, 3vw, 40px)', fontWeight: 700, color: 'white', lineHeight: 1.2, marginBottom: '12px' }}>
            Most TTC platforms track your cycle.{' '}
            <em style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)' }}>OrivonAI tracks your journey.</em>
          </h1>
          <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.8)', lineHeight: 1.6, marginBottom: '20px', maxWidth: '440px' }}>
            Cycle tracking, ovulation analysis, clinically reviewed answers, community matched to your exact stage, and specialist matching — all built around your specific history, not a template for the average woman trying to conceive.
          </p>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              style={{
                background: 'white',
                color: 'rgb(127,119,221)',
                fontSize: '13px',
                fontWeight: 700,
                padding: '10px 20px',
                borderRadius: '24px',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              Join the waitlist
            </button>
          </div>
        </div>
      </div>

      <div id="chapter-track" style={{ ...sectionWhite, ...sectionAnchor }}>
        <div style={inner}>
          <SectionHeader
            act="Track"
            headline="Your cycle, understood."
            italicLine="Not just logged."
            subhead="Nova doesn't just store your data — she reads it, finds your patterns, and tells you what they mean before you think to ask."
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: '24px', alignItems: 'stretch' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {trackFeatures.map((f) => (
                <FeatCard key={f.title} {...f} />
              ))}
            </div>

            <div
              style={{
                background: 'rgb(249,246,241)',
                borderRadius: '16px',
                padding: '20px',
                border: '0.5px solid rgb(232,224,216)',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
              }}
            >
              <p style={{ fontSize: '11px', fontWeight: 700, color: 'rgb(122,107,99)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                What Nova noticed this cycle
              </p>
              <div>
                <div style={{ display: 'flex', height: '32px', borderRadius: '16px', overflow: 'hidden', gap: '2px', marginBottom: '5px' }}>
                  {[
                    ['rgb(200,185,230)', 0.3, 'M'],
                    ['rgb(159,225,203)', 0.4, 'Follicular'],
                    ['rgb(212,83,126)', 0.1, 'O'],
                    ['rgb(244,192,209)', 0.2, 'TWW'],
                  ].map(([bg, flex, label]) => (
                    <div
                      key={label}
                      style={{
                        background: bg,
                        flex,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '10px',
                        fontWeight: 600,
                        color: 'white',
                      }}
                    >
                      {label}
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: 'rgb(136,135,128)' }}>
                  <span>Day 1</span>
                  <span>Day 8</span>
                  <span>Day 14</span>
                  <span>Day 28</span>
                </div>
              </div>

              {oraInsights.map((i) => (
                <div key={i.title} style={{ background: 'white', borderRadius: '10px', padding: '11px', border: '0.5px solid rgb(232,224,216)' }}>
                  <p style={{ fontSize: '11px', fontWeight: 700, color: 'rgb(17,17,17)', marginBottom: '3px' }}>{i.title}</p>
                  <p style={{ fontSize: '11px', color: 'rgb(95,94,90)', lineHeight: 1.5, margin: 0 }}>{i.desc}</p>
                </div>
              ))}

              <div
                style={{
                  background: 'rgb(251,234,240)',
                  borderRadius: '10px',
                  padding: '11px',
                  border: '0.5px solid rgba(212,83,126,0.2)',
                }}
              >
                <p style={{ fontSize: '11px', fontWeight: 700, color: 'rgb(153,53,86)', marginBottom: '3px' }}>💡 Nova&apos;s pattern note</p>
                <p style={{ fontSize: '11px', color: 'rgb(107,58,82)', lineHeight: 1.5, margin: 0 }}>
                  Your fertile window is shifting earlier. You may be ovulating before Day 10 next month — worth tracking closely.
                </p>
              </div>

              <div style={{ background: 'white', borderRadius: '10px', border: '0.5px solid rgb(232,224,216)', overflow: 'hidden' }}>
                <div style={{ padding: '8px 12px', borderBottom: '1px solid rgb(232,224,216)', fontSize: '11px', fontWeight: 700, color: 'rgb(122,107,99)' }}>
                  📄 Nova suggests reading
                </div>
                {oraArticles.map((a, i) => (
                  <div
                    key={a.title}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '8px 12px',
                      borderBottom: i < oraArticles.length - 1 ? '0.5px solid rgb(232,224,216)' : 'none',
                    }}
                  >
                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'rgb(212,83,126)', flexShrink: 0 }} />
                    <div>
                      <p style={{ fontSize: '12px', fontWeight: 500, color: 'rgb(17,17,17)', margin: 0 }}>{a.title}</p>
                      <p style={{ fontSize: '10px', color: 'rgb(122,107,99)', marginTop: '1px', marginBottom: 0 }}>Clinically reviewed · {a.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ height: '1px', background: 'rgb(232,224,216)' }} />

      <div id="chapter-learn" style={{ ...sectionCream, ...sectionAnchor }}>
        <div style={inner}>
          <SectionHeader
            act="Learn"
            headline="Education that knows"
            italicLine="where you are today."
            subhead="Not an article library. Answers tied to your cycle day, your history, and your specific question — so nothing feels generic."
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '24px', alignItems: 'stretch' }}>
            <div
              style={{
                background: 'rgb(255,253,249)',
                border: '0.5px solid rgb(232,224,216)',
                borderRadius: '16px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div
                style={{
                  padding: '12px 16px',
                  borderBottom: '1.5px solid rgb(212,83,126)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  flexShrink: 0,
                }}
              >
                <NovaAvatarImg chapter="ttc" size={30} />
                <div>
                  <p style={{ fontSize: '12px', fontWeight: 600, color: 'rgb(17,17,17)', margin: 0 }}>Nova — your OrivonAI companion</p>
                  <p style={{ fontSize: '10px', color: 'rgb(122,107,99)', margin: 0 }}>Trying to Conceive · Available 24/7</p>
                </div>
                <div style={{ marginLeft: 'auto', width: '7px', height: '7px', borderRadius: '50%', background: 'rgb(29,158,117)' }} />
              </div>

              <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '10px', flexShrink: 0 }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                  <NovaAvatarImg chapter="ttc" size={24} />
                  <div
                    style={{
                      background: 'white',
                      border: '0.5px solid rgb(232,224,216)',
                      borderRadius: '0 10px 10px 10px',
                      padding: '9px 12px',
                      fontSize: '12px',
                      color: 'rgb(17,17,17)',
                      lineHeight: 1.5,
                    }}
                  >
                    You&apos;re on Day 22 — 8 days past ovulation. How are you feeling? What&apos;s going on?
                  </div>
                </div>
                <div
                  style={{
                    background: 'rgb(212,83,126)',
                    borderRadius: '10px 0 10px 10px',
                    padding: '9px 12px',
                    fontSize: '12px',
                    color: 'white',
                    lineHeight: 1.5,
                    maxWidth: '75%',
                    alignSelf: 'flex-end',
                  }}
                >
                  I have cramps but it&apos;s too early for my period. Is this normal?
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                  <NovaAvatarImg chapter="ttc" size={24} />
                  <div
                    style={{
                      background: 'white',
                      border: '0.5px solid rgb(232,224,216)',
                      borderRadius: '0 10px 10px 10px',
                      padding: '9px 12px',
                      fontSize: '12px',
                      color: 'rgb(17,17,17)',
                      lineHeight: 1.5,
                    }}
                  >
                    At 8DPO, implantation cramping is possible — it typically happens 6-10 days after ovulation and is usually milder than period
                    cramps. Progesterone-related cramps are also common at this stage. Both are normal at Day 22.
                  </div>
                </div>
                <div
                  style={{
                    background: 'rgb(212,83,126)',
                    borderRadius: '10px 0 10px 10px',
                    padding: '9px 12px',
                    fontSize: '12px',
                    color: 'white',
                    lineHeight: 1.5,
                    maxWidth: '75%',
                    alignSelf: 'flex-end',
                  }}
                >
                  What if it&apos;s negative?
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                  <NovaAvatarImg chapter="ttc" size={24} />
                  <div
                    style={{
                      background: 'white',
                      border: '0.5px solid rgb(232,224,216)',
                      borderRadius: '0 10px 10px 10px',
                      padding: '9px 12px',
                      fontSize: '12px',
                      color: 'rgb(17,17,17)',
                      lineHeight: 1.5,
                    }}
                  >
                    A negative at 8DPO doesn&apos;t mean this cycle didn&apos;t work — it&apos;s just too early. If your period
                    doesn&apos;t arrive by Day 30, test again. And if this cycle is a no, I&apos;ll be here. We&apos;ll look at
                    your data together and figure out what to try next.
                  </div>
                </div>
              </div>

              <div style={{ flex: 1 }} />

              <div style={{ padding: '8px 16px', display: 'flex', gap: '6px', flexWrap: 'wrap', borderTop: '0.5px solid rgb(232,224,216)', flexShrink: 0 }}>
                {['Is spotting normal at 8DPO?', 'What is implantation?', 'When can I test?'].map((p) => (
                  <span
                    key={p}
                    style={{
                      fontSize: '10px',
                      color: 'rgb(153,53,86)',
                      border: '1px solid rgba(212,83,126,0.3)',
                      borderRadius: '20px',
                      padding: '4px 10px',
                      background: 'white',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {p}
                  </span>
                ))}
              </div>

              <div
                style={{
                  padding: '12px 16px',
                  borderTop: '0.5px solid rgb(232,224,216)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  flexShrink: 0,
                }}
              >
                <span style={{ flex: 1, fontSize: '13px', color: 'rgb(136,135,128)' }}>Ask Nova anything about your journey…</span>
                <span
                  style={{
                    background: 'rgb(212,83,126)',
                    color: 'white',
                    fontSize: '11px',
                    fontWeight: 700,
                    padding: '7px 14px',
                    borderRadius: '20px',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                  }}
                >
                  Open In Nova
                </span>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {learnFeatures.map((f) => (
                <FeatCard key={f.title} {...f} />
              ))}
            </div>
          </div>
        </div>
      </div>

      <div style={{ height: '1px', background: 'rgb(232,224,216)' }} />

      <div id="chapter-connect" style={{ ...sectionWhite, ...sectionAnchor }}>
        <div style={inner}>
          <SectionHeader
            act="Connect"
            headline="People who get it."
            italicLine="Not a Reddit thread from last year."
            subhead="Nova matches you to women at your exact stage — same cycle day, same situation — and opens live rooms for the moments that can't wait."
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', alignItems: 'stretch' }}>
            <div
              style={{
                background: 'rgb(255,253,249)',
                border: '0.5px solid rgb(232,224,216)',
                borderRadius: '14px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <CardHead title="Nova Spaces" sub="Matched to your stage and cycle" right={<span style={{ fontSize: '11px', color: 'rgb(212,83,126)' }}>See all</span>} />
              {oraSpaces.map((s) => (
                <div key={s.name} style={commRow}>
                  <div
                    style={{
                      width: '30px',
                      height: '30px',
                      borderRadius: '8px',
                      background: 'rgb(251,234,240)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      flexShrink: 0,
                    }}
                  >
                    {s.emoji}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: '13px', fontWeight: 500, color: 'rgb(17,17,17)', margin: 0 }}>{s.name}</p>
                    <p style={{ fontSize: '11px', color: 'rgb(122,107,99)', marginTop: '1px', marginBottom: 0 }}>{s.desc}</p>
                  </div>
                  <span
                    style={{
                      fontSize: '10px',
                      fontWeight: 600,
                      padding: '2px 8px',
                      borderRadius: '20px',
                      background: s.tagBg,
                      color: s.tagColor,
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}
                  >
                    {s.tag}
                  </span>
                </div>
              ))}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  padding: '14px 16px',
                  background: 'linear-gradient(120deg,rgba(250,212,229,0.4),rgba(212,240,232,0.4))',
                  borderTop: '1.5px solid rgb(212,83,126)',
                  flex: 1,
                }}
              >
                <NovaAvatarImg chapter="ttc" size={24} />
                <div>
                  <p style={{ fontSize: '12px', fontWeight: 600, color: 'rgb(90,32,64)', marginBottom: '3px' }}>Nova is active in every space</p>
                  <p style={{ fontSize: '11px', color: 'rgb(107,58,82)', lineHeight: 1.55, margin: 0 }}>
                    She reads every thread in real time — surfacing clinical articles, redirecting anxiety spirals, and correcting misinformation
                    before it spreads.
                  </p>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ background: 'rgb(255,253,249)', border: '0.5px solid rgb(232,224,216)', borderRadius: '14px', overflow: 'hidden' }}>
                <CardHead
                  title="Nova Live Rooms"
                  sub="Happening right now"
                  right={
                    <span
                      style={{
                        background: 'rgb(29,158,117)',
                        color: 'white',
                        fontSize: '9px',
                        fontWeight: 700,
                        padding: '2px 6px',
                        borderRadius: '10px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '3px',
                      }}
                    >
                      <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: 'white', display: 'inline-block' }} />
                      Live
                    </span>
                  }
                />
                {liveRooms.map((r, i) => (
                  <div
                    key={r.name}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '10px 16px',
                      borderBottom: i < liveRooms.length - 1 ? '0.5px solid rgb(232,224,216)' : 'none',
                    }}
                  >
                    <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: r.color, flexShrink: 0 }} />
                    <div>
                      <p style={{ fontSize: '13px', fontWeight: 500, color: 'rgb(17,17,17)', margin: 0 }}>{r.name}</p>
                      <p style={{ fontSize: '11px', color: 'rgb(122,107,99)', marginTop: '1px', marginBottom: 0 }}>{r.sub}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div
                style={{
                  background: 'rgb(255,253,249)',
                  border: '0.5px solid rgb(232,224,216)',
                  borderRadius: '14px',
                  overflow: 'hidden',
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <CardHead
                  title={
                    <>
                      Nova Connections{' '}
                      <span
                        style={{
                          fontSize: '9px',
                          fontWeight: 700,
                          background: 'rgb(225,245,238)',
                          color: 'rgb(15,110,86)',
                          padding: '1px 6px',
                          borderRadius: '8px',
                          marginLeft: '6px',
                        }}
                      >
                        New
                      </span>
                    </>
                  }
                  sub="Same cycle day · Nova-matched"
                  right={<span style={{ fontSize: '11px', color: 'rgb(212,83,126)' }}>See all</span>}
                />
                {connections.map((c, i) => (
                  <div
                    key={c.name}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '10px 16px',
                      borderBottom: i < connections.length - 1 ? '0.5px solid rgb(232,224,216)' : 'none',
                    }}
                  >
                    <div
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        background: c.bg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '11px',
                        fontWeight: 700,
                        color: 'white',
                        flexShrink: 0,
                      }}
                    >
                      {c.initials}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: '13px', fontWeight: 500, color: 'rgb(17,17,17)', margin: 0 }}>{c.name}</p>
                      <p style={{ fontSize: '11px', color: 'rgb(122,107,99)', marginTop: '1px', marginBottom: 0 }}>{c.desc}</p>
                    </div>
                    <span
                      style={{
                        fontSize: '10px',
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: '20px',
                        background: 'rgb(225,245,238)',
                        color: 'rgb(15,110,86)',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                      }}
                    >
                      {c.match}
                    </span>
                    <span
                      style={{
                        fontSize: '11px',
                        fontWeight: 600,
                        color: 'rgb(212,83,126)',
                        border: '0.5px solid rgb(212,83,126)',
                        borderRadius: '20px',
                        padding: '3px 9px',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        marginLeft: '4px',
                        cursor: 'pointer',
                      }}
                    >
                      Connect
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ height: '1px', background: 'rgb(232,224,216)' }} />

      <div id="chapter-decide" style={{ ...sectionCream, ...sectionAnchor }}>
        <div style={inner}>
          <SectionHeader
            act="Decide"
            headline="Know what to do next."
            italicLine="Not just what's normal."
            subhead="When it's time to see a specialist, Nova prepares you — so you walk in with the right questions, the right data, and the right expectations."
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', alignItems: 'stretch' }}>
            <div
              style={{
                background: 'rgb(255,253,249)',
                border: '0.5px solid rgb(232,224,216)',
                borderRadius: '16px',
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <p style={{ fontSize: '11px', fontWeight: 700, color: 'rgb(122,107,99)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '12px' }}>
                Your path forward
              </p>
              {pathForward.map((step, i) => (
                <div
                  key={step.title}
                  style={{
                    display: 'flex',
                    gap: '14px',
                    alignItems: 'flex-start',
                    padding: '11px 0',
                    borderBottom: i < pathForward.length - 1 ? '0.5px solid rgb(232,224,216)' : 'none',
                  }}
                >
                  <div
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: 'rgb(251,234,240)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '10px',
                      fontWeight: 700,
                      color: 'rgb(212,83,126)',
                      flexShrink: 0,
                      marginTop: '1px',
                    }}
                  >
                    {i + 1}
                  </div>
                  <div>
                    <p style={{ fontSize: '13px', fontWeight: 600, color: 'rgb(17,17,17)', marginBottom: '2px' }}>{step.title}</p>
                    <p style={{ fontSize: '12px', color: 'rgb(95,94,90)', lineHeight: 1.45, margin: 0 }}>{step.desc}</p>
                    <p style={{ fontSize: '11px', color: 'rgb(212,83,126)', marginTop: '3px', fontStyle: 'italic', marginBottom: 0 }}>{step.nova}</p>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {decideFeatures.map((f) => (
                <FeatCard key={f.title} {...f} />
              ))}
            </div>
          </div>
        </div>
      </div>

      <div style={{ background: 'rgb(127,119,221)', padding: '52px 48px', textAlign: 'center' }}>
        <p style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', marginBottom: '10px' }}>
          Start your TTC journey
        </p>
        <h2 className="serif" style={{ fontSize: 'clamp(24px, 2.5vw, 30px)', fontWeight: 700, color: 'white', lineHeight: 1.25, marginBottom: '10px' }}>
          I&apos;m here for your TTC journey.
          <br />
          <em style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)' }}>What would help you today?</em>
        </h2>
        <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.75)', maxWidth: '440px', margin: '0 auto 24px', lineHeight: 1.6 }}>
          Cycle tracking, fertile windows, and the two-week wait — Nova starts with where you are right now.
        </p>
        <button style={{ background: 'white', color: 'rgb(127,119,221)', fontSize: '13px', fontWeight: 700, padding: '11px 28px', borderRadius: '24px', border: 'none', cursor: 'pointer' }}>
          Start Free With Nova
        </button>
      </div>

      <SiteFooter linkColor="var(--muted)" />
    </div>
  );
}
